import { GetStaticProps } from 'next';

import BlogArticle from '../../../components/blog/BlogArticle';
import { getBlogPost } from '../../../content/blog/posts';
import { DefaultLocale } from '../../../providers/tolgee';

const Post32PercentGrowthInStaarScoresInTexas = () => {
  const post = getBlogPost('32-percent-growth-in-staar-scores-in-texas');
  return (
    <BlogArticle post={post}>
      <figure>
        <img
          src="/blog/32-percent-growth-in-staar-scores-in-texas/inline-1.png"
          alt={
            'STAAR Meets and Masters at a South Texas middle school: 6th grade from 46 percent to 84 percent, 7th grade from 56 percent to 78 percent, and 8th grade from 63 percent to 56 percent between 2022-23 and 2024-25.'
          }
        />
      </figure>
      <h2>{'Year One: Breakthrough in 6th Grade'}</h2>
      <p>
        {
          'Ms. G has dedicated years to supporting her students. She built relationships with families. She stayed late to support struggling readers. She worked tirelessly to make learning engaging.'
        }
      </p>
      <p>
        {
          'Still, nearly half of her students weren’t meeting grade-level expectations on the state’s Reading Language Arts (RLA) assessment. She knew something had to change.'
        }
      </p>
      <p>
        {
          '“I could be doing cartwheels to get their attention, and even that wouldn’t work,” she recalled.'
        }
      </p>
      <p>
        {
          'Then, in the spring of 2023, Ms. G tried something new: she used Curipod for writing instruction and STAAR-aligned test prep. What started as an experiment quickly became a classroom transformation.'
        }
      </p>
      <blockquote>
        {
          '“I just needed something that would grab them—and it did. Every single lesson blew their minds.”'
        }
      </blockquote>
      <p>
        {
          'Students who rarely engaged were suddenly competing for top-voted responses. They shared ideas, analyzed writing, and revised their work—in the same class period. With real-time feedback and engaging standards-aligned prompts, writing became less about pressure and more about progress.'
        }
      </p>
      <p>
        {
          'The breakthrough wasn’t just in the scores. It was in the confidence, consistency, and curiosity that students developed in the classroom every day.'
        }
      </p>
      <h2>{'What Made the Difference?'}</h2>
      <h3>{'Student Agency:'}</h3>
      <p>
        {
          'Students wrote about what mattered to them—Taylor Swift, the World Cup, their favorite food—while mastering state-aligned skills.'
        }
      </p>
      <h3>{'Real-Time Feedback:'}</h3>
      <p>
        {
          'Curipod gave personalized, immediate feedback on every response. Students then analyzed their feedback and made thoughtful changes in real time.'
        }
      </p>
      <h3>{'Culture of Learning:'}</h3>
      <p>
        {
          'Students read each other’s work, debated scores, and worked to improve. What was once a solitary activity became a collaborative experience of shared growth.'
        }
      </p>
      <p>
        {
          'Ms. G’s principal saw the buzz firsthand: “I saw students eager and excited to learn! And actually wanting to write... I get goosebumps. Just look at the scores. That’s the bottom line.” By May, all three sixth-grade teachers were using Curipod’s Test Prep lessons. In 6th grade, students meeting or exceeding expectations jumped from 46% to 55%, a 22% increase from the year before.'
        }
      </p>
      <h2>{'Year Two: Classroom Experiment to School-Wide Movement'}</h2>
      <p>
        {
          'Seeing the growth in 6th grade, the whole school started buzzing about Curipod. It wasn’t just about adopting a new platform. It was about equipping teachers to bring the energy, feedback, and confidence-building strategies to their students that Ms. G achieved in her classroom.'
        }
      </p>
      <p>
        {
          '“I told the team, ‘Just try it once a week,’” Ms. G said. “When the kids lit up, teachers were all in.”'
        }
      </p>
      <blockquote>
        {
          '“Curipod helped us make writing less intimidating,” one teacher shared. “It wasn’t about getting it perfect. It was about getting better.”'
        }
      </blockquote>
      <p>
        {
          'With Curipod, STAAR prep wasn’t high-stakes cramming. It was embedded throughout the year, blending accountability with joyful learning. Confidence rose. Engagement deepened. Teachers felt re-energized. And writing scores didn’t just rise, they soared to a level previously considered unreachable.'
        }
      </p>
      <ul>
        <li>
          {'6th Grade: From 46% to 84% Meets & Masters (in two years with Curipod)'}
        </li>
        <li>{'7th Grade: From 57% to 78% (in one year with Curipod)'}</li>
        <li>{'8th Grade: From 42% to 56% (in one year with Curipod)'}</li>
      </ul>
      <p>
        {
          'Curipod didn’t just get students ready for the test. It got them excited to show up and show what they know. “This isn’t just about writing,” said Ms. G. “It’s about giving students a reason to believe in their own thinking.”'
        }
      </p>
      <p>
        {
          'Great teachers work day after day to provide high-impact instruction that builds confidence, sparks engagement, and grows real skills. Curipod makes it easier for teachers to do what they do best—and see the impact of their efforts in student achievement.'
        }
      </p>
    </BlogArticle>
  );
};

export const getStaticProps: GetStaticProps = async () => {
  return {
    props: {
      _tolgeeSSR: {
        language: DefaultLocale,
      },
    },
  };
};

export default Post32PercentGrowthInStaarScoresInTexas;
